import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader } from '@atlaskit/form';
import { Task, UpdateStatus } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { SlackChannelLink } from '../common';
import { ModalTitle } from '../constants/modal-titles';
import { unAssignTask as UNASSIGN_TASK } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface RequestModalProps {
  task: Task;
  onComplete?: () => void;
  closeModal: () => void;
}

const UnclaimModal: FC<RequestModalProps> = ({ task, onComplete, closeModal }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [unAssignTask] = useMutation<{ unAssignTask: { status: UpdateStatus } }>(UNASSIGN_TASK);
  const userProfile = useApplicationContext();

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'unclaim-modal-notification'
  );

  const handleFormSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const result = await unAssignTask({
        variables: {
          taskId: task.taskId,
          id: userProfile?.userId?.split('@')?.[0],
        },
      });
      const errorMsg = result?.errors?.[0]?.extensions?.message;
      if (errorMsg) {
        throw new Error(errorMsg);
      }
      const status = result?.data?.unAssignTask?.status ?? '';
      if (status === 'OK') {
        onComplete?.();
        renderSuccessNotification(
          'You unassigned the request',
          `The request ${task.taskNumber} has been unassigned and moved to Group requests.`
        );
      }
    } catch (e: any) {
      renderErrorNotification(
        'We’re unable to unassign the request',
        <ErrorNotificationMessage errorMessage={e.message} />
      );
    } finally {
      setLoading(false);
    }
  }, [task.taskId]);

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="unclaim-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="modal-title">{ModalTitle.UNASSIGN_TASK}</span>
                </div>
              }
            />
            <p>
              This action will be added to the request in the Slack channel{' '}
              <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
            </p>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" autoFocus type="submit" isLoading={loading} testId="submit-btn">
                  Unassign request
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default UnclaimModal;
